// banners.js — Setup banner (cross-machine install nudge) + Decrypt
// banner (HMAC key import nudge).
//
// Setup banner picks between two modes:
//   - Install: no daemon detected, or this team has zero machines
//     reporting in. Shows the one-line install snippet.
//   - Bound elsewhere: a local daemon is running but it's pointed at
//     a different team than the dashboard. Shows a "Switch" button
//     (if the daemon knows about this team) or a "Login on the
//     daemon" hint (if it doesn't).
//
// Decrypt banner shows when state.hmacKey is null — the dashboard
// is rendering hashed IDs and the user hasn't dismissed the banner
// for this team.
//
// Both banners persist their dismiss in localStorage keyed by team_id
// so dismissing on team A doesn't suppress them on team B. A session-
// scoped window flag (_setupBannerDismissed / _decryptBannerDismissed)
// stops the banner from re-flashing mid-session between dismiss and
// the next render tick.
//
// updateSetupBanner + updateDecryptBanner are exported and called
// directly from bootstrap.js's renderAll + boot orchestration. The
// dismiss-button click handler also lives in this module via module-
// init wireListeners.
//
// Two click branches stay in bootstrap because they cross into
// bootstrap-internal helpers:
//   - #setup-switch-btn → calls switchLocalDaemonTeam(tid)
//   - #decrypt-banner-import → calls tryAutoImportHmacKey()
//   - #decrypt-banner-settings → calls navigate('settings')
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2m).
import { $ } from "./dom.js";
import { state } from "./state.js";

function bannerDismissKey() {
  const tid = state.teamInfo && state.teamInfo.team_id;
  return tid ? "ac-banner-dismissed:" + tid : "";
}

export function updateSetupBanner() {
  const banner = $("setup-banner");
  if (!banner) return;

  // Persistent dismiss per team_id. Falls back to the legacy
  // session-storage flag for users who dismissed pre-v0.14.
  const dismissKey = bannerDismissKey();
  if (dismissKey) {
    try {
      if (localStorage.getItem(dismissKey) === "1") {
        banner.style.display = "none";
        return;
      }
    } catch {}
  }
  if (window._setupBannerDismissed) {
    banner.style.display = "none";
    return;
  }

  // Linked: any machine has reported in.
  const hasEverConnected = (state.members || []).some((m) => m.last_seen);
  if (hasEverConnected) {
    banner.style.display = "none";
    return;
  }

  // Pick install vs bound-elsewhere. localDaemonCurrentTeam is set
  // by probeLocalDaemon(); empty when the probe failed (no daemon on
  // this machine, or wrong port, or PNA blocked).
  const dashboardTeam = state.teamInfo && state.teamInfo.team_id;
  const localTeam = state.localDaemonCurrentTeam || "";
  const localTeamName = state.localDaemonCurrentTeamName || "";
  const knownTeamIDs = state.localDaemonTeamIDs || new Set();
  const installEl = $("setup-banner-install");
  const elsewhereEl = $("setup-banner-bound-elsewhere");
  if (localTeam && localTeam !== dashboardTeam) {
    // Bound elsewhere — pick switchable vs needs-login.
    if (installEl) installEl.style.display = "none";
    if (elsewhereEl) elsewhereEl.style.display = "";
    const nameEl = $("setup-bound-team-name");
    if (nameEl) nameEl.textContent = localTeamName || "another team";

    const switchableEl = $("setup-banner-switchable");
    const needsLoginEl = $("setup-banner-needs-login");
    const daemonKnowsThisTeam = dashboardTeam && knownTeamIDs.has(dashboardTeam);
    if (switchableEl)
      switchableEl.style.display = daemonKnowsThisTeam ? "" : "none";
    if (needsLoginEl)
      needsLoginEl.style.display = daemonKnowsThisTeam ? "none" : "";
  } else {
    // Install (or no daemon, or daemon already bound here but no
    // member rows yet — the install path's two commands are still
    // the right CTA).
    if (installEl) installEl.style.display = "";
    if (elsewhereEl) elsewhereEl.style.display = "none";
  }
  banner.style.display = "";
}

function decryptBannerDismissKey() {
  const tid = state.teamInfo && state.teamInfo.team_id;
  return tid ? "ac-decrypt-banner-dismissed:" + tid : "";
}

export function updateDecryptBanner() {
  const banner = $("decrypt-banner");
  if (!banner) return;
  if (state.hmacKey) {
    banner.style.display = "none";
    return;
  }
  const blocked = !!state.localNetworkBlocked;
  $("decrypt-banner-default").style.display = blocked ? "none" : "";
  $("decrypt-banner-blocked").style.display = blocked ? "" : "none";
  $("decrypt-banner-dismiss").style.display = blocked ? "none" : "";
  // A browser block hides every name on the dashboard, so an earlier
  // dismiss of the generic nudge must not hide the one way to fix it.
  if (blocked) {
    banner.style.display = "";
    return;
  }
  if (window._decryptBannerDismissed) {
    banner.style.display = "none";
    return;
  }
  const dk = decryptBannerDismissKey();
  if (dk) {
    try {
      if (localStorage.getItem(dk) === "1") {
        banner.style.display = "none";
        return;
      }
    } catch {}
  }
  banner.style.display = "";
}

// ---- module init: dismiss-button click handlers ----

function wireListeners() {
  // Legacy: pre-v0.14 stored the dismiss flag in sessionStorage with
  // no per-team key. We've moved to localStorage keyed by team_id.
  // Wipe the old flag on load so it doesn't shadow the new logic.
  try {
    sessionStorage.removeItem("ac-setup-banner-dismissed");
  } catch {}

  document.addEventListener("click", (e) => {
    if (e.target.id === "setup-banner-dismiss") {
      // Persistent, per-team dismiss. Survives reloads. Re-enable via
      // Settings → "Show install banners again".
      const key = bannerDismissKey();
      if (key) {
        try {
          localStorage.setItem(key, "1");
        } catch {}
      }
      // Also set the legacy session flag so the rest of this session
      // doesn't blink the banner before the next render.
      window._setupBannerDismissed = true;
      updateSetupBanner();
      return;
    }
    if (e.target.id === "decrypt-banner-dismiss") {
      const key = decryptBannerDismissKey();
      if (key) {
        try {
          localStorage.setItem(key, "1");
        } catch {}
      }
      window._decryptBannerDismissed = true;
      updateDecryptBanner();
      return;
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
